import { createSelector } from '@ngrx/store';
import { featureKey, SubmissionState } from './submission.reducer';
import { Thread } from '@data-access/submissions';

export const selectActiveSubmissionId = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => submissionState.activeSubmissionId,
);

export const selectActiveSubmissionStage = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) =>
    submissionState.submission?.current_stage,
);

export const selectSubmission = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => submissionState?.submission,
);

export const selectSubmissionList = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => submissionState.submissionList,
);

export const selectLoadingSubmission = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => submissionState.loadingSubmission,
);

export const selectLoadingSubmissionList = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => submissionState.loadingSubmissionList,
);

export const selectWorkspaceTypes = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => submissionState.workspaceTypes,
);

export const selectLoadingWorkspaceTypes = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => submissionState.loadingWorkspaceTypes,
);

export const selectHasNotificationsDetails = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => submissionState.hasNotificationsDetails,
);

export const selectHasNotificationsDocuments = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) =>
    submissionState.hasNotificationsDocuments,
);

export const selectLoadingMessages = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => submissionState.loadingMessages,
);

export const selectOpenThreads = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => {
    if (submissionState.threads) {
      return submissionState.threads.filter((message) => !message.isResolved);
    }
    return [];
  },
);

export const selectIfExistinThreads = createSelector(
  (state: any) => state[featureKey],
  (submissionState, props = { entityId: '' }) => {
    if (submissionState.threads) {
      return submissionState.threads?.find(
        (thread: Thread) => thread.entityId === props.entityId,
      );
    }
    return false;
  },
);
export const selectAllThreads = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => submissionState.threads,
);

export const selectThread = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => submissionState.activeThread,
);

export const selectReviewMaterialDocuments = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => submissionState.reviewMaterialDocuments,
);

export const selectMaterialDocumentTypes = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) =>
    submissionState.requiredMaterialDocumentTypes,
);

export const selectReviewMaterialDocumentsLoading = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) =>
    submissionState.loadingReviewMaterialDocuments,
);

export const selectMaterialDocumentTypesLoading = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) =>
    submissionState.loadingRequiredMaterialDocumentTypes,
);

export const selectLoadingEntityDocuments = (
  workspaceId: string,
  entityId: string,
) =>
  createSelector(
    (state: any) => state[featureKey],
    (submissionState: SubmissionState) =>
      submissionState.uploadingReviewerDocument[workspaceId]?.[entityId],
  );

export const selectReviewerDocumentById = (documentId: string) =>
  createSelector(selectReviewMaterialDocuments, (documents) =>
    documents?.find((doc) => doc.id === documentId),
  );

export const selectHasOpenDocumentThreads = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => {
    if (submissionState.threads) {
      return submissionState.threads.some(
        (message) => !message.isResolved && message.entityType === 'document',
      );
    }
    return false;
  },
);

export const selectOpenDocumentThreadsCount = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) => {
    if (submissionState.threads) {
      return submissionState.threads.filter(
        (message) => !message.isResolved && message.entityType === 'document',
      ).length;
    }
    return 0;
  },
);

export const selectSitesForSubmission = (submissionId: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (submissionState: SubmissionState) => {
      if (!submissionState.sites || !submissionId) {
        return [];
      }
      return submissionState.sites || [];
    },
  );

export const selectSitesForStudy = (studyId: string) =>
  createSelector(
    (state: any) => state[featureKey],
    (submissionState: SubmissionState) => {
      if (!submissionState.studySites || !studyId) {
        return [];
      }
      return submissionState.studySites;
    },
  );

export const getWorkspaceSitesListLength = createSelector(
  (state: any) => state[featureKey],
  (submissionState: SubmissionState) =>
    submissionState.submission?.site_ids?.length,
);
